import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { PieChart } from '@ovComponents/2-component/pieChart/pieChart';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { formatCompactNumber } from '@utils/formatting';
import { formatPercentValue } from '@utils/moneyFormatting';
import { isUndefined } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AumExplorerGroupByTypes } from './aumExplorer';

export const AumExplorerPie = ({
  loading,
  sData,
  totalValue,
  groupBy,
  colors,
}: {
  loading: boolean;
  sData: any[];
  totalValue: number;
  groupBy: AumExplorerGroupByTypes;
  colors: (`#${string}` | undefined)[];
}) => {
  const { t } = useTranslation(['homePageConfiguration', 'accountTypes']);
  const [activeItem, setActiveItem] = useState<any>(null);
  const { sys } = useThemeTokens();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Box
          display='flex'
          position='relative'
          alignItems='center'
          justifyContent='center'
          pl={{ xs: 3, md: 10 }}
          pr={{ xs: 3, md: 10 }}
          pt={3}
          pb={3}
        >
          {loading ? (
            <Skeleton variant='circular' height='400px' width='400px' />
          ) : (
            <PieChart
              datasets={[
                {
                  label: '',
                  data: sData.map((x: any) => x.value),
                },
              ]}
              labels={sData.map((x: any) => t(x.key))}
              onHover={(index: number | undefined) =>
                setActiveItem(!isUndefined(index) ? sData[index] : null)
              }
              customColors={colors}
            />
          )}
          {!loading && (
            <Box
              textAlign='center'
              position='absolute'
              left='50%'
              top='50%'
              sx={{ transform: 'translate(-50%,-50%)' }}
            >
              {activeItem ? (
                <>
                  <Typography
                    data-testid={'active-pie-chart-holdings-asset-title'}
                    sx={{ color: sys.color.onSurfaceVariant }}
                    variant='bodyLarge'
                  >
                    {activeItem.key}
                  </Typography>
                  <Typography
                    data-testid='active-pie-chart-holdings-value'
                    variant='headingLarge'
                  >
                    {formatCompactNumber((activeItem?.value ?? 0) / 100, {
                      isCurrency: true,
                    })}
                  </Typography>
                  <Typography
                    data-testid='active-pie-chart-holdings-percentage'
                    weight='bold'
                    variant='bodyLarge'
                  >
                    {(activeItem?.value ?? 0) >= 0 &&
                      `${formatPercentValue((activeItem?.value || 0) / totalValue)}`}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    data-testid='pie-chart-holdings-asset-title'
                    sx={{ color: sys.color.onSurfaceVariant }}
                    variant='bodyLarge'
                  >
                    {t(`aumExplorer.${groupBy}`)}
                  </Typography>
                  <Typography
                    data-testid='pie-chart-holdings-value'
                    variant='headingLarge'
                  >
                    {formatCompactNumber(totalValue / 100, {
                      isCurrency: true,
                    })}
                  </Typography>
                  <Typography
                    data-testid='pie-chart-holdings-percentage'
                    weight='bold'
                    variant='bodyLarge'
                  >
                    100%
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box
          display='flex'
          justifyContent='center'
          flexDirection='column'
          minHeight='100%'
        >
          {loading && (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              height='100%'
              flexDirection='column'
            >
              {[...Array(5)].map((_, ind) => (
                <Skeleton
                  key={ind}
                  variant='rectangular'
                  height='50px'
                  width='100%'
                  sx={{ mb: '2px' }}
                />
              ))}
            </Box>
          )}
          {!loading &&
            sData.map((x: any, i: number) => {
              const activeIndex = sData.findIndex(
                (y: any) => activeItem?.key === y.key,
              );
              return (
                <Box
                  data-testid={`pie-chart-holdings-tile-${i}`}
                  key={x.translatedName?.en ? `${x.translatedName.en}-${i}` : i}
                  onMouseEnter={() => setActiveItem(x)}
                  onMouseLeave={() => setActiveItem(undefined)}
                  display='flex'
                  justifyContent='space-between'
                  sx={{
                    borderBottom:
                      activeItem?.key === x.key ||
                      (activeIndex > -1 && activeIndex - 1 === i) ||
                      i + 1 === sData.length
                        ? `2px solid ${sys.color.surface}`
                        : `2px solid ${sys.color.surfaceContainer}`,
                    p: 1.5,
                    backgroundColor:
                      activeItem?.key === x.key
                        ? sys.color.surfaceContainer
                        : 'none',
                    borderRadius:
                      activeItem?.key === x.key ? sys.borderRadius.md : '0px',
                  }}
                >
                  <Box
                    display='flex'
                    flexDirection='row'
                    justifyContent='start'
                    alignItems='center'
                  >
                    <Box
                      sx={{
                        borderRadius: '10px',
                        height: '8px',
                        width: '8px',
                        backgroundColor: colors[i % colors.length],
                        mr: 1,
                      }}
                    />
                    <Typography variant='bodyLarge'>{x.key}</Typography>
                  </Box>
                  <Typography
                    variant='bodyLarge'
                    weight='bold'
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatPercentValue(x.value / totalValue)}
                  </Typography>
                </Box>
              );
            })}
        </Box>
      </Grid>
    </Grid>
  );
};
